/**
 * RC8 Phase 1 · Slice 7 · C3 Sensitive Identity · Biometric Gate.
 *
 * Canonical biometric-authorization abstraction. ONE gate serves every
 * C3 reveal surface — passport, boarding pass, emergency contact,
 * loyalty full number. Never a per-surface reimplementation.
 *
 * Governance (authoritative):
 *   · ADR-MOBILE-002 §6.4  — biometric gate; each sensitive reveal is
 *                            a fresh biometric event; passcode fallback
 *                            allowed per Accessibility Standard §7.6.
 *   · ADR-MOBILE-002 §7.2  — sensitive-surface roster.
 *   · ADR-MOBILE-003 §3.2.C3 — separate encrypted store; biometric
 *                              access control on the record key;
 *                              immediate eviction on session revoke;
 *                              secure wipe.
 *
 * Non-negotiable rules encoded here:
 *   1. Authorization NEVER derives from a persisted boolean. Every
 *      reveal call goes through a real native authentication event.
 *   2. When biometrics are unavailable, we FAIL CLOSED. No fallback
 *      to a client-side "unlocked" flag, no password-only reveal.
 *   3. C3 payload is decrypted and returned INSIDE the gate call.
 *      There is no separate `get(...)` path that could bypass the
 *      biometric prompt.
 *
 * Preview vs native behaviour:
 *   · Capacitor native → real Face ID / Touch ID / Fingerprint via
 *     `@aparajita/capacitor-biometric-auth`. Secure storage via
 *     `@aparajita/capacitor-secure-storage` (Keychain / EncryptedSharedPreferences).
 *   · Web preview      → NO SILENT SUCCESS. `requestSensitiveIdentityAccess`
 *     returns `{ok: false, reason: "biometric_unavailable"}`. There is
 *     no simulated-success path anywhere in this file.
 */
import { PLATFORM } from "./platform";
import { BiometricAuth } from "@aparajita/capacitor-biometric-auth";
import { SecureStorage } from "@aparajita/capacitor-secure-storage";


// Prefix for the secure-storage keys. Every C3 entry lives under
// `cc.rc8.c3::${user_id}::${voyage_id}::${sub_key}`. The key is stored
// in the OS Keychain / Keystore — never in localStorage, never in
// IndexedDB, never in plain SQLite.
const C3_PREFIX = "cc.rc8.c3";


function c3Key(userId, voyageId, subKey) {
  if (!userId) throw new Error("C3 gate: user_id required");
  if (!voyageId) throw new Error("C3 gate: voyage_id required");
  if (!subKey) throw new Error("C3 gate: sub_key required");
  return `${C3_PREFIX}::${userId}::${voyageId}::${subKey}`;
}


/**
 * Probe the native biometric surface. Returns an authoritative
 * capability report; NEVER speculates.
 */
export async function inspectBiometric() {
  if (!PLATFORM.is_native) {
    return {
      is_native: false,
      is_available: false,
      biometry_type: null,
      reason: "web_preview",
    };
  }
  try {
    const info = await BiometricAuth.checkBiometry();
    return {
      is_native: true,
      is_available: !!info.isAvailable,
      biometry_type: info.biometryType || null,
      reason: info.reason || null,
    };
  } catch (e) {
    return {
      is_native: true,
      is_available: false,
      biometry_type: null,
      reason: `native_probe_failed:${String(e)}`,
    };
  }
}


/**
 * Perform a fresh biometric authentication.  Returns `{ok, reason}`.
 *
 * Failure paths (all fail closed):
 *   · biometric_unavailable — device has no biometric enrolled OR
 *     we're in the web preview.
 *   · biometric_cancelled  — traveller dismissed the prompt.
 *   · biometric_failed     — enrolled biometric did not match.
 *   · biometric_error      — plugin-level exception.
 *
 * There is NO ok:true path anywhere without a real native
 * authentication event. `BiometricAuth.authenticate()` throws on
 * failure — we catch and translate to `{ok:false, reason}`.
 */
async function _authenticate({ reason }) {
  if (!PLATFORM.is_native) {
    return { ok: false, reason: "biometric_unavailable" };
  }
  const probe = await inspectBiometric();
  if (!probe.is_available) {
    return { ok: false, reason: "biometric_unavailable" };
  }
  try {
    await BiometricAuth.authenticate({
      reason: reason || "Reveal sensitive identity information",
      cancelTitle: "Cancel",
      allowDeviceCredential: true,   // Accessibility Standard §7.6 · device passcode fallback
      // Never persist an "unlocked" flag between prompts.
      // Each call is a fresh authentication event.
    });
    return { ok: true, reason: null };
  } catch (e) {
    // Plugin throws typed errors. Translate to our closed vocabulary.
    const code = (e && e.code) || "";
    if (code === "userCancel" || code === "cancelled") {
      return { ok: false, reason: "biometric_cancelled" };
    }
    if (code === "authenticationFailed") {
      return { ok: false, reason: "biometric_failed" };
    }
    return { ok: false, reason: `biometric_error:${code || String(e)}` };
  }
}


// ---------------------------------------------------------------------------
// Provisioning · called by the sync layer while the traveller is online
// AND authenticated. NEVER creates a payload on its own — the caller
// must supply the server-authoritative value.
// ---------------------------------------------------------------------------

/**
 * Provision a C3 record.  Requires a live native environment;
 * refuses to write anything in the web preview.
 *
 * @param {object} scope { user_id, voyage_id, sub_key }
 * @param {string|object} payload server-authoritative sensitive value
 * @returns {Promise<{ok: boolean, reason?: string}>}
 */
export async function provisionSensitiveIdentity(scope, payload) {
  if (!PLATFORM.is_native) {
    return { ok: false, reason: "c3_protected_storage_unavailable" };
  }
  if (payload === undefined || payload === null) {
    return { ok: false, reason: "c3_empty_payload_refused" };
  }
  try {
    const key = c3Key(scope.user_id, scope.voyage_id, scope.sub_key);
    // SecureStorage on native routes to Keychain (iOS) /
    // EncryptedSharedPreferences (Android). No plaintext trace.
    await SecureStorage.set(
      key,
      typeof payload === "string" ? payload : JSON.stringify(payload),
      /* sync */ false,
      /* access */ "afterFirstUnlockThisDeviceOnly",
    );
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: `c3_provision_failed:${String(e)}` };
  }
}


/**
 * The ONLY read path to a C3 record. Executes:
 *   1. Fresh biometric authentication (fails closed on any error).
 *   2. Secure-storage read — key protected by device credential.
 *   3. Returns the plaintext payload ONLY on both success paths.
 *
 * `ok:false` never carries a payload. Callers must not fall back to
 * an ordinary storage read.
 *
 * @param {object} scope   { user_id, voyage_id, sub_key }
 * @param {object} options { reason?: string prompt copy for the OS dialog }
 */
export async function requestSensitiveIdentityAccess(scope, options = {}) {
  // Web preview / non-native: cannot satisfy the biometric contract.
  if (!PLATFORM.is_native) {
    return { ok: false, reason: "biometric_unavailable", data: null };
  }
  // 1) Fresh biometric event. This is the ONE gate.
  const auth = await _authenticate({
    reason: options.reason || "Reveal sensitive identity information",
  });
  if (!auth.ok) {
    return { ok: false, reason: auth.reason, data: null };
  }
  // 2) Secure-storage read — post-biometric.
  try {
    const key = c3Key(scope.user_id, scope.voyage_id, scope.sub_key);
    const raw = await SecureStorage.get(key);
    if (raw === null || raw === undefined) {
      return { ok: false, reason: "c3_not_provisioned", data: null };
    }
    // Payload may be a stringified JSON object or a raw string.
    let data;
    try { data = JSON.parse(raw); } catch { data = raw; }
    return { ok: true, reason: null, data };
  } catch (e) {
    return { ok: false, reason: `c3_read_failed:${String(e)}`, data: null };
  }
}


/**
 * Secure wipe of a C3 record. ADR-MOBILE-003 §3.2.C3 · "eviction is a
 * secure wipe — overwrite before delete". We overwrite the record
 * value with a nulling sentinel before removing the key so any raw
 * key material lingering in the storage journal is invalidated.
 */
export async function purgeSensitiveIdentity(scope) {
  if (!PLATFORM.is_native) return { ok: true, purged: 0 };
  try {
    const key = c3Key(scope.user_id, scope.voyage_id, scope.sub_key);
    // Overwrite with a zero-length string, then remove.
    try { await SecureStorage.set(key, "", false, "afterFirstUnlockThisDeviceOnly"); } catch {}
    await SecureStorage.remove(key);
    return { ok: true, purged: 1 };
  } catch (e) {
    return { ok: false, reason: `c3_purge_failed:${String(e)}` };
  }
}


/**
 * Purge every C3 record for a given (user, voyage) tuple. Called on
 * session revoke and voyage archive per ADR-MOBILE-003 §3.2.C3.
 */
export async function purgeSensitiveIdentityForVoyage({ user_id, voyage_id }) {
  if (!PLATFORM.is_native) return { ok: true, purged: 0 };
  try {
    const keys = await SecureStorage.keys();
    const prefix = `${C3_PREFIX}::${user_id}::${voyage_id}::`;
    let purged = 0;
    for (const k of (keys || [])) {
      if (k.startsWith(prefix)) {
        try { await SecureStorage.set(k, "", false, "afterFirstUnlockThisDeviceOnly"); } catch {}
        await SecureStorage.remove(k);
        purged += 1;
      }
    }
    return { ok: true, purged };
  } catch (e) {
    return { ok: false, reason: `c3_purge_voyage_failed:${String(e)}` };
  }
}


// Expose constants for tests / consumers that need to reference the
// prefix without importing SecureStorage.
export const C3_STORAGE_PREFIX = C3_PREFIX;
